/**
 * Player shot-trajectory preview: a thin line from the muzzle tracing where the
 * next shell will go — INCLUDING ricochets — so you can plan bank shots. It
 * mirrors the shell's exact reflection logic (gamemap reflectCircle + the floor's
 * road-edge bounce) at the player's level, for the player's current bounce count,
 * so the preview can't lie. One reused buffer geometry; no per-frame allocation.
 */

import * as THREE from "three";
import { BULLET } from "../config";
import { reflectCircle } from "../world/gamemap";
import type { Arena } from "../world/arena";

const MAX_PTS = 10; // muzzle + up to (maxBounces) corners + endpoint, with headroom

export class AimLine {
  private line: THREE.Line;
  private positions: Float32Array;
  private readonly _p = { x: 0, z: 0 };
  private readonly _v = { x: 0, z: 0 };
  private _arena: Arena | null = null; // set per-update so the reflect probe needs no closure

  constructor(scene: THREE.Scene, color: number) {
    this.positions = new Float32Array(MAX_PTS * 3);
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    geom.setDrawRange(0, 0);
    const mat = new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.55, depthWrite: false });
    this.line = new THREE.Line(geom, mat);
    this.line.frustumCulled = false;
    this.line.renderOrder = 2; // over the floor/decks
    this.line.visible = false;
    scene.add(this.line);
  }

  hide(): void { this.line.visible = false; }

  /**
   * Trace the shot path and draw it. (mx,my,mz)=muzzle, (dirx,dirz)=aim dir,
   * level=the player's level, bounces=remaining ricochets, maxRange=how far the
   * shell can travel before it expires, stepDist=the shell's per-substep travel
   * THIS frame (speed·dt/substeps) so the preview samples walls at the SAME depth.
   *
   * Why match the shell's step exactly rather than trace finely: reflectCircle
   * picks the bounce FACE from how deep the shell has penetrated the box when it's
   * checked. The real shell checks once per ~1-stud substep; a finer trace checks
   * shallower and, at a corner/grazing hit, can pick the OTHER face — so the
   * predicted ricochet would point a different way than the shell actually flies.
   * Sampling at the shell's own stride keeps the preview honest.
   */
  update(mx: number, my: number, mz: number, dirx: number, dirz: number, level: number, bounces: number, maxRange: number, arena: Arena | null, stepDist: number): void {
    const pos = this._p, vel = this._v;
    this._arena = arena;
    const len = Math.hypot(dirx, dirz) || 1;
    pos.x = mx; pos.z = mz;
    vel.x = dirx / len; vel.z = dirz / len;
    let n = 0;
    this.put(n++, mx, my, mz);
    let bl = bounces, traveled = 0, diedAtWall = false;
    // Clamp the stride: never below a floor (keeps iteration bounded if dt→0) and
    // never past 2·BULLET.radius (so a stride can't tunnel a wall undetected).
    const step = Math.min(2 * BULLET.radius - 0.1, Math.max(0.25, stepDist));
    while (traveled < maxRange && n < MAX_PTS - 1) {
      pos.x += vel.x * step; pos.z += vel.z * step; traveled += step;
      if (this.reflectAt(level)) {
        this.put(n++, pos.x, my, pos.z); // bounce corner (or the death wall)
        if (--bl < 0) { diedAtWall = true; break; } // shell spent — it stops here
      }
    }
    if (!diedAtWall && n < MAX_PTS) this.put(n++, pos.x, my, pos.z); // endpoint (range cap)
    for (let i = n; i < MAX_PTS; i++) this.put(i, pos.x, my, pos.z); // pad unused
    this.line.geometry.setDrawRange(0, n);
    (this.line.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
    this.line.visible = true;
  }

  /** One reflection probe at the current _p/_v — mirrors the shell's wall logic
   *  exactly (gamemap solids + the floor's road edges). Mutates _p/_v on a bounce. */
  private reflectAt(level: number): boolean {
    let hit = reflectCircle(this._p, this._v, BULLET.radius, level === 0);
    if (this._arena && this._arena.reflectShell(this._p, this._v, BULLET.radius, level)) hit = true;
    return hit;
  }

  private put(i: number, x: number, y: number, z: number): void {
    this.positions[i * 3] = x; this.positions[i * 3 + 1] = y; this.positions[i * 3 + 2] = z;
  }
}
